import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import OrderItemsDetail from '../components/OrderItemsDetail/OrderItemsDetail';
import { formatDate, formatPrice, formatItemsSummary, getStatusLabel } from '../utils/orderUtils';
import { formatShippingAddressLines } from '../utils/addressUtils';
import { fetchMyOrders } from '../services/ordersApi';
import './OrdersPage.css';

const sortOrders = (orders) =>
  [...orders].sort((a, b) => {
    const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
    const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
    return bTime - aTime;
  });

const dedupeOrders = (orders) => {
  const map = new Map();
  orders.forEach((order) => map.set(order.id, order));
  return Array.from(map.values());
};

const fetchOrdersFromFirestore = async (currentUser) => {
  const byUser = query(
    collection(db, 'orders'),
    where('userId', '==', currentUser.uid)
  );
  const userSnap = await getDocs(byUser);
  let fetched = userSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const email = currentUser.email?.trim();
  if (email) {
    const normalized = email.toLowerCase();
    const emailsToTry = normalized === email ? [email] : [normalized, email];

    for (const targetEmail of emailsToTry) {
      const byEmail = query(
        collection(db, 'orders'),
        where('customerEmail', '==', targetEmail)
      );
      const emailSnap = await getDocs(byEmail);
      fetched = fetched.concat(emailSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
  }

  return sortOrders(dedupeOrders(fetched));
};

const StatusBadge = ({ status, localDelivery }) => (
  <span className={`orders-status orders-status--${status}`}>
    {getStatusLabel(status, localDelivery)}
  </span>
);

const OrderCard = ({ order }) => {
  const [open, setOpen] = useState(false);
  const addressLines = formatShippingAddressLines(order.shippingAddress);

  return (
    <div className="orders-card">
      <button className="orders-card-summary" onClick={() => setOpen((v) => !v)}>
        <div className="orders-card-col">
          <span className="orders-label">Data</span>
          <span>{formatDate(order.createdAt)}</span>
        </div>
        <div className="orders-card-col">
          <span className="orders-label">Itens</span>
          <span>{formatItemsSummary(order.items)}</span>
        </div>
        <div className="orders-card-col">
          <span className="orders-label">Total</span>
          <span className="orders-total">{formatPrice(order.total)}</span>
        </div>
        <div className="orders-card-col">
          <span className="orders-label">Status</span>
          <StatusBadge status={order.status} localDelivery={order.localDelivery} />
        </div>
        <span className="orders-toggle">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="orders-card-detail">
          {order.status === 'cancelado' && (
            <div className="orders-cancelled-banner">
              Pedido cancelado — reembolso de {formatPrice(order.total)} iniciado.
              O valor retorna para a mesma forma de pagamento em alguns dias úteis.
            </div>
          )}

          <div className="orders-detail-grid">
            <div>
              <p className="orders-detail-title">Itens do pedido</p>
              <OrderItemsDetail
                items={order.items}
                shippingService={order.shippingService}
                shippingCost={order.shippingCost}
                total={order.total}
              />
            </div>

            <div>
              <p className="orders-detail-title">Endereço de entrega</p>
              {addressLines ? (
                <div className="orders-address">
                  {addressLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              ) : (
                <p className="orders-no-address">Endereço não disponível</p>
              )}

              {order.shippedAt && (
                <p className="orders-shipped-at">
                  Enviado em: {formatDate(order.shippedAt)}
                </p>
              )}

              {order.trackingCode && (
                <div className="orders-tracking">
                  <p className="orders-detail-title" style={{ marginTop: '1rem' }}>Rastreamento</p>
                  <a
                    href={`https://rastreamento.correios.com.br/app/index.php?objeto=${order.trackingCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="orders-tracking-link"
                  >
                    {order.trackingCode} — Rastrear
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrdersPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    const loadOrders = async () => {
      setError('');
      try {
        const idToken = await currentUser.getIdToken();
        const fromApi = await fetchMyOrders(idToken);
        setOrders(fromApi);
      } catch (apiErr) {
        console.warn('API de pedidos indisponível, tentando Firestore:', apiErr.message);
        try {
          setOrders(await fetchOrdersFromFirestore(currentUser));
        } catch (err) {
          console.error('Erro ao buscar pedidos:', err);
          setError('Não foi possível carregar seus pedidos. Tente novamente mais tarde.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [currentUser, navigate]);

  return (
    <div className="orders-page">
      <div className="orders-container">
        <h1 className="orders-title">Meus Pedidos</h1>

        {loading && <p className="orders-loading">Carregando...</p>}
        {error && <p className="orders-error">{error}</p>}

        {!loading && !error && orders.length === 0 && (
          <div className="orders-empty">
            <p>Você ainda não fez nenhum pedido.</p>
            <button className="btn btn-filled" onClick={() => navigate('/catalog')}>
              <span>Ver produtos</span>
            </button>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div className="orders-list">
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
